import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import DramaCard from "@/components/feature/DramaCard";
import FilterRow from "@/pages/category/components/FilterRow";
import { dramas } from "@/mocks/dramas";
import { categories, regionOptions, sortOptions, statusOptions, yearOptions } from "@/mocks/site";
import { useSeo } from "@/hooks/useSeo";
import { categoryLabel, filterDramas, sortDramas } from "@/utils/drama";

const PAGE_SIZE = 12;

const typeOptions = [{ key: "all", label: "全部类型" }, ...categories.map((item) => ({ key: item.key, label: item.label }))];

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [visible, setVisible] = useState(PAGE_SIZE);

  const type = searchParams.get("type") || "all";
  const region = searchParams.get("region") || "all";
  const year = searchParams.get("year") || "all";
  const status = searchParams.get("status") || "all";
  const sort = searchParams.get("sort") || "new";
  const keyword = searchParams.get("q") || "";

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [type, region, year, status, sort, keyword]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === "all" || (key === "sort" && value === "new")) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  };

  const results = useMemo(() => {
    const filtered = filterDramas(dramas, { type, region, year, status, q: keyword });
    return sortDramas(filtered, sort);
  }, [type, region, year, status, sort, keyword]);

  const shown = results.slice(0, visible);
  const channelName = type === "all" ? "全部短剧" : categoryLabel(type);
  const pageTitle = `${channelName}${keyword ? `「${keyword}」` : ""} - 微短剧高清在线观看 | 剧焰短剧`;

  useSeo({
    title: pageTitle,
    description: `剧焰短剧${channelName}频道，收录${channelName}相关的热门微短剧共 ${results.length} 部，支持按地区、年份、连载状态与评分排序，高清全集在线观看。`,
    keywords: `${channelName},${channelName}短剧,微短剧在线观看,短剧全集,短剧排行榜`,
    path: "/category",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `${channelName}微短剧列表`,
      description: `${channelName}频道收录的微短剧合集`,
      inLanguage: "zh-CN",
      mainEntity: {
        "@type": "ItemList",
        numberOfItems: results.length,
        itemListElement: results.slice(0, 12).map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.title,
          url: `https://www.juyan-drama.com/drama/${item.id}`,
        })),
      },
    },
  });

  return (
    <>
      <SiteHeader />
      <main className="w-full px-4 md:px-6">
        {/* 面包屑 */}
        <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-1.5 pt-5 text-xs text-foreground-500 md:text-sm">
          <Link to="/" className="cursor-pointer transition-colors hover:text-primary-300">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-sm text-foreground-700" aria-hidden="true" />
          <span className="text-foreground-200">剧集分类</span>
          {type !== "all" && (
            <>
              <i className="ri-arrow-right-s-line text-sm text-foreground-700" aria-hidden="true" />
              <span className="text-primary-300">{channelName}</span>
            </>
          )}
        </nav>

        <header className="mt-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl font-black text-foreground-50 md:text-3xl">
              {keyword ? `搜索「${keyword}」` : `${channelName}频道`}
            </h1>
            <p className="mt-2 text-xs text-foreground-500 md:text-sm">
              共 <strong className="font-semibold text-primary-300">{results.length}</strong> 部短剧，
              按条件筛选后可直接进入播放页观看
            </p>
          </div>
          <div className="flex items-center gap-1.5 rounded-full border border-foreground-50/10 bg-foreground-50/[0.04] p-1">
            {sortOptions.map((option) => {
              const active = option.key === sort;
              return (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => updateParam("sort", option.key)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors md:text-sm ${
                    active ? "bg-primary-500 font-medium text-background-950" : "text-foreground-400 hover:text-primary-300"
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </header>

        {/* 多维筛选器 */}
        <section
          aria-label="剧集筛选条件"
          className="mt-5 divide-y divide-foreground-50/[0.07] rounded-2xl border border-foreground-50/10 bg-foreground-50/[0.025] px-4 py-1 md:px-5"
        >
          <FilterRow label="类型" options={typeOptions} value={type} onChange={(value) => updateParam("type", value)} />
          <FilterRow label="地区" options={regionOptions} value={region} onChange={(value) => updateParam("region", value)} />
          <FilterRow label="年份" options={yearOptions} value={year} onChange={(value) => updateParam("year", value)} />
          <FilterRow label="状态" options={statusOptions} value={status} onChange={(value) => updateParam("status", value)} />
        </section>

        {/* 剧集瀑布流列表 */}
        <section aria-labelledby="result-heading" className="mt-6 md:mt-8">
          <h2 id="result-heading" className="sr-only">
            筛选结果列表
          </h2>

          {shown.length > 0 ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 md:gap-5 xl:grid-cols-6">
              {shown.map((drama) => (
                <DramaCard key={drama.id} drama={drama} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-foreground-50/15 bg-foreground-50/[0.02] px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-foreground-50/[0.06] text-foreground-500">
                <i className="ri-search-eye-line text-2xl" aria-hidden="true" />
              </span>
              <p className="mt-4 text-sm text-foreground-200">没有找到符合条件的短剧</p>
              <p className="mt-1 text-xs text-foreground-500">试试更换筛选条件，或清空关键词重新搜索</p>
              <button
                type="button"
                onClick={() => setSearchParams({}, { replace: true })}
                className="mt-5 cursor-pointer whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-xs font-bold text-background-950 transition-colors hover:bg-primary-400"
              >
                重置全部筛选
              </button>
            </div>
          )}

          {visible < results.length && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border border-foreground-50/15 bg-foreground-50/[0.04] px-6 py-2.5 text-sm text-foreground-200 transition-colors hover:border-primary-400/60 hover:text-primary-300"
              >
                <i className="ri-add-line text-base" aria-hidden="true" />
                加载更多（剩余 {results.length - visible} 部）
              </button>
            </div>
          )}
        </section>

        {/* SEO 文本块 */}
        <section aria-labelledby="category-seo" className="mt-12 rounded-2xl border border-foreground-50/[0.08] bg-foreground-50/[0.02] p-5 md:mt-16 md:p-7">
          <h2 id="category-seo" className="font-heading text-base font-bold text-foreground-100 md:text-lg">
            关于{channelName}短剧
          </h2>
          <p className="mt-2 text-xs leading-relaxed text-foreground-500 md:text-sm">
            {channelName}是微短剧市场中播放量最高的题材之一，节奏紧凑、单集时长短，非常适合碎片时间观看。剧焰短剧为你整理了该频道下正在连载与已完结的全部剧集，并提供按年份、地区与评分筛选的能力，帮助你快速找到口碑作品。
          </p>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}